import React, {
   createContext,
   useContext,
   useEffect,
   useState,
   useRef,
} from "react";
import getCookie from "../getCookie";
import { useNavigate } from "react-router-dom";
import { fetchSQS, serviceQuery } from "../requests";
import { ToastContext } from "./ToastContext";
import { translate } from "../Translate";

export const AuthContext = createContext();

export function AuthContextProvider(props) {
   const navigate = useNavigate();
   const addToast = useContext(ToastContext);

   const refreshIntervalRef = useRef(null);
   const licenseCheckRef = useRef(null);
   const isLoggingOutRef = useRef(false);

   const [credentials, setCredentials] = useState(
      getCookie("refreshtoken")
         ? {
              refreshToken: getCookie("refreshtoken"),
              eclToken: getCookie("ecltoken"),
              userID: getCookie("userID"),
           }
         : null,
   );
   const [license, setLicense] = useState(localStorage.getItem("license"));

   function startRefreshInterval(refreshToken) {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);

      refreshIntervalRef.current = setInterval(() => {
         fetchSQS("servicequery/RefreshTokenMessage", "POST", {
            query: { refreshToken },
         })
            .then((res) => {
               if (res.Payload?.result === "Success") {
                  setCredentials({
                     refreshToken: res.Payload.refreshToken,
                     eclToken: res.Payload.token,
                     userID: res.Payload.userId,
                  });
               } else {
                  logout();
               }
            })
            .catch(() => logout());
      }, 600000);
   }

   useEffect(() => {
      if (isLoggingOutRef.current) return;

      // clear old interval if it exists
      if (licenseCheckRef.current) {
         clearInterval(licenseCheckRef.current);
      }

      if (license && credentials) {
         licenseCheckRef.current = setInterval(() => {
            fetchSQS("servicequery/LicenseWebActiveMessage", "POST", {
               query: {},
            })
               .then((res) => {
                  if (res.Status !== 0) {
                     if (res.Payload && !res.Payload.hasLicenese) {
                        localStorage.removeItem("license");
                        addToast(
                           translate("License revoked by Administrator"),
                           "error",
                        );
                        setLicense(null);
                     } else {
                        localStorage.removeItem("license");
                        addToast(
                           translate("License server unavailable"),
                           "error",
                        );
                        logout();
                     }
                  }
               })
               .catch((error) => {
                  console.error(error);
                  localStorage.removeItem("license");
                  addToast(translate("License server unavailable"), "error");
                  logout();
               });
         }, 90000);
      }

      return () => {
         if (licenseCheckRef.current) {
            clearInterval(licenseCheckRef.current);
            licenseCheckRef.current = null;
         }
      };
   }, [license, credentials]);

   useEffect(() => {
      const savedRefreshToken = getCookie("refreshtoken");
      if (!savedRefreshToken) return;

      // Attempt to refresh tokens immediately when app loads
      fetchSQS("servicequery/RefreshTokenMessage", "POST", {
         query: { refreshToken: savedRefreshToken },
      })
         .then((res) => {
            if (res.Payload?.result === "Success") {
               // Tokens refreshed successfully
               setCredentials({
                  refreshToken: res.Payload.refreshToken,
                  eclToken: res.Payload.token,
                  userID: res.Payload.userId,
               });

               // Start the normal refresh interval
               startRefreshInterval(res.Payload.refreshToken);
            } else {
               logout();
            }
         })
         .catch(() => {
            logout();
         });
   }, []);

   async function logout() {
      isLoggingOutRef.current = true;

      if (refreshIntervalRef.current) {
         clearInterval(refreshIntervalRef.current);
         refreshIntervalRef.current = null;
      }
      if (licenseCheckRef.current) {
         clearInterval(licenseCheckRef.current);
         licenseCheckRef.current = null;
      }

      localStorage.removeItem("license");
      setLicense(null);
      setCredentials(null);
      sessionStorage.clear();

      serviceQuery({
         message: "LicenseKick",
         query: { user: credentials?.userID },
      })
         .catch(console.error)
         .finally(() => {
            fetchSQS("auth/logout")
               .catch(console.error)
               .finally(() => {
                  // Clear refresh interval safely
                  if (refreshIntervalRef.current) {
                     clearInterval(refreshIntervalRef.current);
                     refreshIntervalRef.current = null;
                  }

                  setCredentials(null);
                  sessionStorage.clear();
                  // window.localStorage.removeItem("sso");
                  isLoggingOutRef.current = false;
               });
         });
   }

   async function login(username, password, needsOTP, OTPCode) {
      const params = needsOTP
         ? { user: username, password, code: OTPCode }
         : { user: username, password, cookie: getCookie("deviceId") };

      const loginStatus = await fetchSQS("auth/login", "POST", params);

      if (
         loginStatus.Status !== 0 ||
         loginStatus?.Payload?.result !== "Success"
      ) {
         if (loginStatus?.Payload?.result === "Requires2fa") {
            addToast(
               translate("Please enter OTP code from an authorized device"),
            );
            return "OTP";
         }
         addToast(translate("Authentication error"));
         return null;
      }

      // Clear old interval if still hanging around
      if (refreshIntervalRef.current) {
         clearInterval(refreshIntervalRef.current);
      }

      // Start new refresh interval
      startRefreshInterval(loginStatus.Payload.refreshToken);

      setCredentials({
         refreshToken: loginStatus.Payload.refreshToken,
         eclToken: loginStatus.Payload.token,
         userID: loginStatus.Payload.userId,
      });

      const urlParams = new URLSearchParams(window.location.search);
      navigate({ pathname: "/sourceone/", search: urlParams.toString() });

      return null;
   }

   return (
      <AuthContext.Provider
         value={{
            credentials,
            setCredentials,
            logout,
            login,
            license,
            setLicense,
            startRefreshInterval,
         }}
      >
         {props.children}
      </AuthContext.Provider>
   );
}
